/**
 * dem-loader.ts — load a real Digital Elevation Model into a TerrainHeightmap.
 *
 * Supported source formats:
 *   - **r32**  : raw little-endian Float32 binary, length = res*res.
 *   - **r16**  : raw little-endian UInt16 binary; converted via scale/offset.
 *   - **png16**: 16-bit single-channel grayscale PNG, decoded via ImageDecoder.
 *               Requires Chromium 94+ (WebCodecs). Same scale/offset semantics.
 *
 * Pipeline (offline):
 *   GeoTIFF (Geoportal.gov.pl WCS / WMS export)
 *     → gdalwarp -t_srs EPSG:2180 -tr <px> <px> -te <xmin ymin xmax ymax> input.tif clipped.tif
 *     → gdal_translate -ot Float32 -of EHdr clipped.tif out.r32      (raw Float32)
 *       OR
 *     → gdal_translate -ot UInt16  -scale -of PNG  clipped.tif out.png  (16-bit PNG)
 *
 * Runtime: pass a manifest describing the geometry/encoding. The data is
 * resampled (nearest) to the engine resolution if `targetRes` differs.
 */

import type { TerrainHeightmap } from "./procedural-terrain";

export type DEMFormat = "r32" | "r16" | "png16";

export interface DEMManifest {
  /** URL of the binary blob or PNG. */
  url: string;
  format: DEMFormat;
  /** Source resolution (square). */
  resolution: number;
  /** World extent in metres (square, centered at origin). */
  extent: number;
  /**
   * For r16/png16: heightMetres = raw * scale + offset.
   * For r32: ignored (raw is already metres) but `offset` still added.
   */
  scale?: number;
  offset?: number;
  /** Optional engine-side resolution (nearest-resample if different). */
  targetRes?: number;
}

export async function loadDEM(
  manifest: DEMManifest,
): Promise<TerrainHeightmap> {
  const res = manifest.resolution;
  let raw: Float32Array;

  if (manifest.format === "r32") {
    const buf = await (await fetch(manifest.url)).arrayBuffer();
    if (buf.byteLength !== res * res * 4) {
      throw new Error(
        `DEM r32 size mismatch: expected ${res * res * 4} bytes, got ${buf.byteLength}`,
      );
    }
    raw = new Float32Array(buf);
    if (manifest.offset !== undefined && manifest.offset !== 0) {
      const off = manifest.offset;
      for (let i = 0; i < raw.length; i++) raw[i] = raw[i]! + off;
    }
  } else if (manifest.format === "r16") {
    const buf = await (await fetch(manifest.url)).arrayBuffer();
    if (buf.byteLength !== res * res * 2) {
      throw new Error(
        `DEM r16 size mismatch: expected ${res * res * 2} bytes, got ${buf.byteLength}`,
      );
    }
    const u16 = new Uint16Array(buf);
    raw = u16ToFloat(u16, manifest.scale ?? 1, manifest.offset ?? 0);
  } else {
    raw = await decodePng16(
      manifest.url,
      res,
      manifest.scale ?? 1,
      manifest.offset ?? 0,
    );
  }

  let data = raw;
  let resolution = res;
  if (manifest.targetRes && manifest.targetRes !== res) {
    data = nearestResample(raw, res, manifest.targetRes);
    resolution = manifest.targetRes;
  }

  return { data, resolution, extent: manifest.extent };
}

function u16ToFloat(
  src: Uint16Array,
  scale: number,
  offset: number,
): Float32Array {
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) out[i] = src[i]! * scale + offset;
  return out;
}

async function decodePng16(
  url: string,
  res: number,
  scale: number,
  offset: number,
): Promise<Float32Array> {
  // Prefer WebCodecs ImageDecoder which preserves the 16-bit channel as F16.
  const ID = (globalThis as { ImageDecoder?: typeof ImageDecoder })
    .ImageDecoder;
  if (!ID) {
    throw new Error("PNG 16-bit DEM requires the WebCodecs ImageDecoder API");
  }
  const buf = await (await fetch(url)).arrayBuffer();
  const decoder = new ID({ data: buf, type: "image/png" });
  const result = await decoder.decode();
  const frame = result.image;
  if (frame.codedWidth !== res || frame.codedHeight !== res) {
    throw new Error(
      `DEM png16 dim mismatch: expected ${res}, got ${frame.codedWidth}×${frame.codedHeight}`,
    );
  }
  // Copy as gray 16-bit if available; else fall back to RGBA8 (lossy).
  const layout = frame.allocationSize();
  const pixels = new Uint8Array(layout);
  await frame.copyTo(pixels);
  frame.close();
  decoder.close();

  // Heuristic: if format encodes 16-bit gray, byte length = res*res*2.
  if (pixels.byteLength === res * res * 2) {
    const u16 = new Uint16Array(pixels.buffer);
    return u16ToFloat(u16, scale, offset);
  }
  if (pixels.byteLength === res * res * 4) {
    // RGBA8 fallback: pack R,G into 16-bit.
    const out = new Float32Array(res * res);
    for (let i = 0; i < out.length; i++) {
      const v = pixels[i * 4]! | (pixels[i * 4 + 1]! << 8);
      out[i] = v * scale + offset;
    }
    return out;
  }
  throw new Error(
    `DEM png16 unsupported decoded layout: ${pixels.byteLength} bytes`,
  );
}

function nearestResample(
  src: Float32Array,
  srcRes: number,
  dstRes: number,
): Float32Array {
  const out = new Float32Array(dstRes * dstRes);
  for (let j = 0; j < dstRes; j++) {
    const sj = Math.min(srcRes - 1, Math.floor((j / dstRes) * srcRes));
    for (let i = 0; i < dstRes; i++) {
      const si = Math.min(srcRes - 1, Math.floor((i / dstRes) * srcRes));
      out[j * dstRes + i] = src[sj * srcRes + si]!;
    }
  }
  return out;
}

/**
 * Parse a DEM manifest from a URL `?dem=…` query string.
 *
 * Example: `?dem=/dem/lake.r32&fmt=r32&res=1024&extent=2000&scale=1&offset=0&targetRes=1024`
 */
export function manifestFromQuery(search: string): DEMManifest | null {
  const q = new URLSearchParams(search);
  const url = q.get("dem");
  if (!url) return null;
  return {
    url,
    format: (q.get("fmt") as DEMFormat) ?? "r32",
    resolution: parseInt(q.get("res") ?? "1024", 10),
    extent: parseFloat(q.get("extent") ?? "2000"),
    scale: parseFloat(q.get("scale") ?? "1"),
    offset: parseFloat(q.get("offset") ?? "0"),
    targetRes: q.get("targetRes")
      ? parseInt(q.get("targetRes")!, 10)
      : undefined,
  };
}
